import { useState } from 'react';

interface Props {
  onSubmit: (text: string, label: string) => void;
  /** Whether the calling page already has a manuscript loaded (used to vary copy). */
  reason: 'scanned' | 'fallback';
}

const MIN_LENGTH = 800;

export function TextInputCard({ onSubmit, reason }: Props) {
  const [text, setText] = useState('');
  const [label, setLabel] = useState('pasted-text');
  const [open, setOpen] = useState(reason === 'scanned');

  const readyToSubmit = text.trim().length >= MIN_LENGTH;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded border border-slate-300 bg-white px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-50"
      >
        Paste manuscript text instead
      </button>
    );
  }

  return (
    <section className="rounded-md border border-amber-300 bg-amber-50 p-3 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-amber-900">
          {reason === 'scanned' ? 'Scanned PDF detected' : 'Paste manuscript text'}
        </h2>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-[10px] text-slate-500 hover:text-slate-800"
        >
          cancel
        </button>
      </div>
      <p className="mt-1 text-xs text-amber-900">
        {reason === 'scanned'
          ? 'The PDF looks like a scan — pdf.js extracted almost no text. Paste the manuscript text below and ManuscriptSkim will run the pattern engine against it.'
          : 'Paste manuscript text directly. The same pattern engine runs against pasted text.'}
      </p>
      <input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Label (used as filename)"
        className="mt-2 w-full rounded border border-slate-200 px-2 py-1 text-xs"
      />
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={8}
        placeholder="Paste the manuscript text here. Include the title, abstract, and methods at minimum."
        className="mt-2 w-full rounded border border-slate-200 px-2 py-1 text-xs"
      />
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-[10px] text-slate-600">
          {text.trim().length.toLocaleString()} chars
          {!readyToSubmit && ` · need at least ${MIN_LENGTH.toLocaleString()}`}
        </span>
        <button
          type="button"
          disabled={!readyToSubmit}
          onClick={() => onSubmit(text, label.trim() || 'pasted-text')}
          className="rounded bg-slate-700 px-2 py-1 text-[11px] text-white disabled:bg-slate-300"
        >
          Analyse pasted text
        </button>
      </div>
    </section>
  );
}
